import React, { useState } from 'react';
import { studentSharing } from '../../api';
import '../Sharing/ShareStudentModal.css';

/**
 * ShareStudentModal - Allow coaches to share a student with another coach
 * Coaches enter email of another coach and select which student to share
 */
const ShareStudentModal = ({ isOpen, onClose, students, onShareSuccess }) => {
  const [toCoachEmail, setToCoachEmail] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    if (!toCoachEmail.trim()) {
      setError('Please enter coach email');
      setLoading(false);
      return;
    }

    if (!selectedStudentId) {
      setError('Please select a student to share');
      setLoading(false);
      return;
    }

    try {
      await studentSharing.shareStudent(toCoachEmail.trim(), selectedStudentId);
      setSuccess(true);
      setToCoachEmail('');
      setSelectedStudentId('');

      // Call success callback
      if (onShareSuccess) {
        onShareSuccess();
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send share invitation');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedStudent = students.find(s => s._id === selectedStudentId);

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal-content" onClick={e => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2>Share Student With Coach</h2>
          <button className="share-modal-close" onClick={onClose}>×</button>
        </div>

        {success && (
          <div className="share-modal-success">
            ✅ Share invitation sent successfully!
          </div>
        )}

        {error && (
          <div className="share-modal-error">
            ❌ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="share-modal-form">
          <div className="share-form-group">
            <label htmlFor="coach-email">Coach Email</label>
            <input
              id="coach-email"
              type="email"
              placeholder="coach@example.com"
              value={toCoachEmail}
              onChange={(e) => setToCoachEmail(e.target.value)}
              disabled={loading}
              required
            />
            <small>Email of the coach you want to share with</small>
          </div>

          <div className="share-form-group">
            <label htmlFor="student-select">Student to Share</label>
            <select
              id="student-select"
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              disabled={loading}
              required
            >
              <option value="">-- Select a student --</option>
              {students.map(student => (
                <option key={student._id} value={student._id}>
                  {student.name}
                </option>
              ))}
            </select>
          </div>

          {selectedStudent && (
            <div className="share-modal-preview">
              <p>
                <strong>{selectedStudent.name}</strong> will be able to:
              </p>
              <ul>
                <li>📖 View all your notes about {selectedStudent.name}</li>
                <li>✍️ Add their own notes about {selectedStudent.name}</li>
                <li>🔔 See updates when you add new notes</li>
              </ul>
            </div>
          )}

          <div className="share-modal-actions">
            <button
              type="button"
              className="share-modal-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="share-modal-submit"
              disabled={loading || !toCoachEmail || !selectedStudentId}
            >
              {loading ? '📤 Sending...' : '📤 Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShareStudentModal;
